import React from "react";
import { useEffect,useMemo,useState,useCallback,useRef } from "react";
import api from "@/api/axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Area,
  Legend,
  LabelList,
} from "recharts";

import { useAuth } from "@/context/useAuth";
import { Download, Trash2 } from "lucide-react";
import dashboardIcon from "../assets/Frame.svg";
import searchIcon from "@/assets/ri-search-line.svg";
import addIcon from "@/assets/add-line.svg";
import settingsIcon from "@/assets/settings-6-line.svg";
import calenderIcon from "@/assets/ri-calendar-2-line.svg";
import downloadIcon from "@/assets/download.svg";
import archiveIcon from "@/assets/archive.svg";
import arrowRightIcon from "@/assets/arrowRight.svg";
import copyIcon from "@/assets/copyIcon.svg";
import TransactionViewModal from "../pages/projects/tabs/components/TransactionViewModal"

const maskId = (id) => (id ? `......${id.slice(-5)}` : "-");

const normalizeTransaction = (t) => {
  console.log("RAW TX paymentMethod:", t._id, t.paymentMethod);
  return {
    ...t,
    projectName:
      t.projectName ||
      t.project?.name ||
      (t.projectId && typeof t.projectId === "object" && t.projectId.name) ||
      (typeof t.projectId === "string" ? t.projectId : null) ||
      "-",
    paymentMethod:
      t.paymentMethod ||
      t.paymentMode ||
      t.paymentInfo?.paymentMethod ||
      t.paymentInfo?.mode ||
      t.paymentInfo?.method ||
      t.gatewayResponse?.payment_mode ||
      t.gatewayResponse?.method ||
      "-",
  };
};

const formatINRCompact = (amount) => {
  if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(1)}Cr`;
  if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(1)}L`;
  if (amount >= 1e3) return `₹${(amount / 1e3).toFixed(1)}K`;
  return `₹${amount}`;
};

const fillDateGaps = (data, fromDate, toDate) => {
  if (!fromDate || !toDate) return data || [];
  
  const start = new Date(fromDate);
  const end = new Date(toDate);
  
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  const allDates = [];
  const current = new Date(start);
  
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    allDates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  
  const dataMap = new Map((data || []).map(item => [item.date, item]));
  
  return allDates.map(date => ({
    date,
    transactionCount: dataMap.get(date)?.transactionCount || 0,
    totalAmount: dataMap.get(date)?.totalAmount || 0
  }));
};

export default function Dashboard() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [timeFilter, setTimeFilter] = useState("7d");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [gatewaySummary, setGatewaySummary] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [transactionVolume, setTransactionVolume] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendError, setTrendError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const toDateInputRef = useRef(null);
  const pageCacheRef = useRef({});
  const currentRequestRef = useRef(null);
  const allTransactionsRef = useRef(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  const limit = 12;

// useEffect(() => {
//   let attempts = 0;

//   const poll = async () => {
//     attempts++;
//     const res = await api.get("/api/payments?status=created");
//     const cashfreeTxns = res.data.data.filter(t => t.gateway === "cashfree");

//     for (const tx of cashfreeTxns) {
//       await api.get(`/api/payments/verify/${tx.transactionId}`);
//     }

//     if (attempts < 5 && cashfreeTxns.length > 0) {
//       setTimeout(poll, 5000);
//     }
//   };

//   poll();
// }, []);



  const handleConfigureGateway = useCallback(() => {
    console.log("Configure Gateway clicked");
  }, []);

  const formatLastActivity = useCallback((date) => {
    if (!date) return "-";
    const diffMs = Date.now() - new Date(date).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay} day ago`;
    const diffWeek = Math.floor(diffDay / 7);
    return `${diffWeek} wk ago`;
  }, []);

  const getDateRange = useCallback(() => {
    if (timeFilter === "custom" && (dateRange.from || dateRange.to)) {
      return {
        from: dateRange.from
          ? new Date(dateRange.from + "T00:00:00+05:30").toISOString()
          : undefined,
        to: dateRange.to
          ? new Date(dateRange.to + "T23:59:59.999+05:30").toISOString()
          : undefined,
      };
    }

    const now = new Date();
    let from, to;

    if (timeFilter === "7d") {
      from = new Date(now);
      from.setDate(from.getDate() - 6);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    } else if (timeFilter === "month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    } else if (timeFilter === "lastMonth") {
      const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      from = new Date(lastMonthYear, lastMonth, 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(lastMonthYear, lastMonth + 1, 0);
      to.setHours(23, 59, 59, 999);
    } else if (timeFilter === "year") {
      from = new Date(now.getFullYear(), 0, 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    } else {
      from = new Date(now);
      from.setDate(from.getDate() - 6);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    }

    const formatToIST_ISO = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const ms = String(date.getMilliseconds()).padStart(3, '0');
      
      return new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}+05:30`).toISOString();
    };

    return {
      from: formatToIST_ISO(from),
      to: formatToIST_ISO(to),
    };
  }, [timeFilter, dateRange.from, dateRange.to]);

  const loadData = useCallback(async () => {
    const range = getDateRange();
    const cached = pageCacheRef.current[page];

   

    if (!cached) setLoading(true);

    if (currentRequestRef.current) {
      try {
        currentRequestRef.current.abort();
        
      } catch (err) {
        console.log(err)
      }
    }

    const controller = new AbortController();
    currentRequestRef.current = controller;

    try {
      const { data } = await api.get("/api/reports/combined-dashboard", {
        params: {
          ...range,
          page,
          limit,
        },
        signal: controller.signal
      });
      console.log("FIRST TX:", JSON.stringify(data.data.recentTransactions[0], null, 2));
    

      setStats(data.data.stats);
      setSnapshot({
        ...data.data.snapshot,
        activeProjects: data.data.activeProjects,
      });
      setGatewaySummary(data.data.gatewaySummary);

      const normalized = (data.data.recentTransactions || []).map(normalizeTransaction);

      

      pageCacheRef.current[page] = normalized;
     
      if (activePageRef.current === page) {
        setTransactions(normalized);
      } else {
        console.log('[Dashboard] stale response for page', page, 'current page is', activePageRef.current, '- skipping setTransactions');
      }
      setTotalCount(data.data.totalCount || 0);
      
      const volumeData = data.data.transactionVolume || [];
      const filledData = fillDateGaps(volumeData, range.from, range.to);
      setTransactionVolume(filledData);

      if (user.role === "admin") {
        api.get("/api/reports/revenue-trend")
          .then(r => setRevenueTrend(r.data.data))
          .catch(() => setTrendError("Revenue trend unavailable"));
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('[Dashboard] request aborted for page', page);
      } else {
        console.error('[Dashboard] loadData error', error);
      }
    } finally {
      setLoading(false);
   
      if (currentRequestRef.current === controller) {
        currentRequestRef.current = null;
      }
    }
  }, [user.role, page, getDateRange]);

  const prefetchPage = useCallback(async (p) => {
    if (!p || p < 1) return;

    if (pageCacheRef.current[p]) return;

    const range = getDateRange();

    try {
      const controller = new AbortController();
      const { data } = await api.get("/api/reports/combined-dashboard", {
        params: { ...range, page: p, limit },
        signal: controller.signal
      });

      const normalized = (data.data.recentTransactions || []).map(normalizeTransaction);
      pageCacheRef.current[p] = normalized;
      
    } catch (e) {
      if (e.name === 'CanceledError' || e.name === 'AbortError') {
      } else {
        console.warn('[Dashboard] prefetch error for page', p, e?.message || e);
      }
    }
  }, [getDateRange, limit]);

//   useEffect(() => {
//   const params = new URLSearchParams(window.location.search);
//   const txnid = params.get("txnid");
//   if (!txnid) return;

//   api.get(`/api/payments/verify/${txnid}`).finally(() => {
//     window.history.replaceState({}, document.title, window.location.pathname);
//     pageCacheRef.current = {};
//     loadData();  // reload after verify
//   });
// }, [loadData]);

useEffect(() => {
  const timer = setTimeout(() => {
    pageCacheRef.current = {};
    loadData();
  }, 3000);

  return () => clearTimeout(timer);
}, [loadData]);


  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
 
    pageCacheRef.current = {};
  }, [timeFilter, dateRange.from, dateRange.to]);

  useEffect(() => {
    if (!searchQuery.trim()) return;
    if (allTransactionsRef.current) return;

    let mounted = true;
    setSearchLoading(true);

    api.get('/api/payments')
      .then((res) => {
        if (!mounted) return;
        const allTx = (res.data.data || []).map(normalizeTransaction);
        allTransactionsRef.current = allTx;
   
      })
      .catch((err) => {
        console.error('[Dashboard] failed to load all transactions for search', err);
      })
      .finally(() => {
        if (mounted) setSearchLoading(false);
      });

    return () => { mounted = false; };
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery.trim()) setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (timeFilter === "custom") {
      return;
    }

    const now = new Date();
    let from, to;

    if (timeFilter === "7d") {
      from = new Date(now);
      from.setDate(from.getDate() - 6);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    } else if (timeFilter === "month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    } else if (timeFilter === "lastMonth") {
      const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      from = new Date(lastMonthYear, lastMonth, 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(lastMonthYear, lastMonth + 1, 0);
      to.setHours(23, 59, 59, 999);
    } else if (timeFilter === "year") {
      from = new Date(now.getFullYear(), 0, 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    } else {
      from = new Date(now);
      from.setDate(from.getDate() - 6);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      to.setHours(23, 59, 59, 999);
    }

    const formatDateOnly = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setDateRange({
      from: formatDateOnly(from),
      to: formatDateOnly(to),
    });
  }, [timeFilter]);

//   useEffect(() => {
//   api.get("/api/payments?status=created").then(async (res) => {
//     const pending = (res.data.data || []).filter(
//       t => t.gateway === "cashfree" && t.status === "created"
//     );

//     for (const tx of pending) {
//       await api.get(`/api/payments/verify/${tx.transactionId}`);
//     }
//   });
// }, []);


  const visibleTransactions = useMemo(() => {
    if (!searchQuery.trim()) return transactions;

    const q = searchQuery.trim().toLowerCase();

    const source = allTransactionsRef.current || transactions || [];

    if (/^\d+(?:\.\d+)?$/.test(q)) {
      return source.filter(tx => Number(tx.amount) === Number(q));
    }

    return source.filter(tx => 
      (tx.transactionId || '').toLowerCase().includes(q) ||
      (tx.gatewayPaymentId || '').toLowerCase().includes(q)
    );
  }, [transactions, searchQuery]);

  const paginatedTransactions = useMemo(() => {
    if (searchQuery.trim()) {
      const start = (page - 1) * limit;
      const end = start + limit;
      return visibleTransactions.slice(start, end);
    }
    return visibleTransactions;
  }, [visibleTransactions, page, limit, searchQuery]);


  const totalPages = useMemo(() => {
    if (searchQuery.trim()) {
      return Math.ceil(visibleTransactions.length / limit);
    }
    return Math.ceil(totalCount / limit);
  }, [totalCount, limit, searchQuery, visibleTransactions.length]);

  useEffect(() => {
    if (totalPages === 0) {
      if (page !== 1) setPage(1);
    } else if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const activePageRef = useRef(page);
  useEffect(() => {
    activePageRef.current = page;
  }, [page]);

  useEffect(() => {
    const cached = pageCacheRef.current[page];
    
    if (cached) {
    
      setTransactions(cached);
    }
  }, [page]);

  useEffect(() => {
    const total = totalPages || Math.max(1, Math.ceil(totalCount / limit));
    if (page < total) prefetchPage(page + 1);
    if (page > 1) prefetchPage(page - 1);
  }, [page, totalPages, totalCount, limit, prefetchPage]);

  const isSearching = searchQuery.trim().length > 0;

  const handleTimeFilterChange = useCallback((key) => {
    setTimeFilter(key);
    setPage(1);
  }, []);

  const handleDateRangeChange = useCallback((field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
    setTimeFilter("custom");
    setPage(1);
    
    if (field === 'from' && toDateInputRef.current) {
      setTimeout(() => {
        if (toDateInputRef.current && toDateInputRef.current.showPicker) {
          toDateInputRef.current.showPicker();
        }
      }, 100);
    }
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-[10px] space-y-[30px] pb-10">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <img src={dashboardIcon} alt="Dashboard" className="w-6 h-6"/>
          <h1 className="text-[24px] font-semibold leading-[31.2px] text-black">
            Dashboard
          </h1>
        </div>

        <div className="flex items-center gap-1 w-[300px] px-[10px] py-[8px] rounded-[5px] border border-[#C4C4C4]">
          <img src={searchIcon} alt="Search" className="w-[14px] h-[14px]"/>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Transaction"
            className="w-full outline-none bg-transparent text-[12px] text-[#232323] placeholder:text-[#232323]"
          />
        </div>

        <div className="flex items-center gap-[10px]">
          <button
            type="button"
            onClick={() => (window.location.href = `/projects/create`)}
            className="flex items-center gap-1 px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal hover:bg-[#E8E8E8] transition-colors cursor-pointer"
          >
            <img src={addIcon} alt="Add" className="w-4 h-4"/>
            Add Business
          </button>

          <button
            type="button"
            onClick={handleConfigureGateway}
            className="flex items-center gap-1 px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal hover:bg-[#E8E8E8] transition-colors cursor-pointer"
          >
            <img src={settingsIcon} alt="Settings" className="w-4 h-4"/>
            Configure Gateway
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center py-[5px]">
        <div className="flex gap-[6px]">
          {[
            { key: "7d", label: "Last 7 Days" },
            { key: "month", label: "This Month" },
            { key: "lastMonth", label: "Last Month" },
            { key: "year", label: "This Year" },
          ].map((item) => {
            const active = timeFilter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleTimeFilterChange(item.key)}
                className={`px-[10px] py-[8px] rounded-[5px] text-[12px] leading-[15.6px] transition cursor-pointer
                  ${active ? "bg-[#D3E6FC] font-semibold" : "font-normal hover:bg-[#E8E8E8]"}
                  text-[#232323]`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-[4px]">
          <div className="flex items-center gap-[4px] px-[10px] py-[8px] rounded-[5px]">
            <img src={calenderIcon} alt="Calendar" className="w-4 h-4"/>
            <span className="text-[12px] text-[#232323] font-normal">Date Range</span>
          </div>
          <div className="min-w-[180px] flex items-center gap-[8px] px-[16px] py-[8px] bg-white rounded-[5px] border border-[#C4C4C4]">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => handleDateRangeChange('from', e.target.value)}
              onClick={(e) => {
                if (e.currentTarget.showPicker) {
                  e.currentTarget.showPicker();
                }
              }}
              className="text-[12px] font-medium text-[#232323] outline-none cursor-pointer"
            />
            <span className="text-[12px] text-[#757575]">to</span>
            <input
              ref={toDateInputRef}
              type="date"
              value={dateRange.to}
              onChange={(e) => handleDateRangeChange('to', e.target.value)}
              onClick={(e) => {
                if (e.currentTarget.showPicker) {
                  e.currentTarget.showPicker();
                }
              }}
              className="text-[12px] font-medium text-[#232323] outline-none cursor-pointer"
            />
          </div>
        </div>
      </div>

      {!isSearching && (
        <>
          <StatsCards stats={stats} timeFilter={timeFilter} />
          <MidAnalytics
            stats={stats}
            gatewaySummary={gatewaySummary}
            snapshot={snapshot}
            formatLastActivity={formatLastActivity}
          />
          <TransactionVolumeChart 
            data={transactionVolume} 
            fromDate={dateRange.from} 
            toDate={dateRange.to} 
          />
        </>
      )}

      <TransactionTable
        data={paginatedTransactions}
        page={page}
        totalPages={totalPages}
        setPage={setPage}
        loading={loading || searchLoading}
      />
      {user.role === "admin" && (
        <RevenueTrendChart data={revenueTrend} error={trendError} />
      )}
    </div>
  );
}

const StatsCards = React.memo(({ stats, timeFilter }) => {
  const getTimeLabel = useCallback(() => {
    switch(timeFilter) {
      case "7d": return "Last 7 Days";
      case "month": return "This Month";
      case "lastMonth": return "Last Month";
      case "year": return "This Year";
      default: return "Selected Period";
    }
  }, [timeFilter]);

  const cards = useMemo(() => {
    if (!stats) return [];
    
    const timeLabel = getTimeLabel();
    
    return [
      {
        label: `Total TXs (${timeLabel})`,
        value: `${(stats.totalPayments || 0).toLocaleString()} txns`,
      },
      {
        label: `Total Collected (${timeLabel})`,
        value: `₹${(stats.totalAmount || 0).toLocaleString()}`,
      },
      {
        label: "Gateway Success Rate",
        value: "97.8%",
      },
      {
        label: "Gateway Health Score",
        value: "4/5 live",
      },
    ];
  }, [stats, getTimeLabel]);

  if (!stats) return null;

  return (
    <div className="flex gap-[20px]">
      {cards.map((card, i) => (
        <div
          key={i}
          className="flex-1 bg-[#F1F1F1] rounded-[10px] border border-[#E6E6E6] px-[10px] py-[6px]"
        >
          <div className="flex justify-between items-center">
            <div className="px-[6px] py-[3px]">
              <span className="text-[12px] font-normal text-[#232323] leading-[22px]">
                {card.label}
              </span>
            </div>
            <div className="px-[6px] py-[4px]">
              <span className="text-[12px] font-bold text-[#232323] leading-[22px] capitalize">
                {card.value}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

const MidAnalytics = React.memo(({ stats, gatewaySummary, snapshot, formatLastActivity }) => {
  if (!stats || !snapshot) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-4">
      <div className="self-stretch p-5 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] inline-flex flex-col justify-center items-start gap-2.5">
        <div className="py-[5px] inline-flex justify-center items-center gap-2.5">
          <div className="text-black text-sm font-semibold leading-[18.2px]">
            Payment Insights
          </div>
        </div>
        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.5px] outline-[#E6E6E6]" />
        <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-[#232323] text-xs font-medium leading-[15.6px]">
              Avg. Tx Duration
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {snapshot.avgTxDurationSec ? `${snapshot.avgTxDurationSec}s` : "-"}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Payment Volume Trend
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {snapshot.paymentVolumeTrend
                ? `${snapshot.paymentVolumeTrend.percent}% ${snapshot.paymentVolumeTrend.label}`
                : "-"}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Most Used Payment Method
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {snapshot.mostUsedPaymentMethod
                ? `${snapshot.mostUsedPaymentMethod.method} (₹${Math.round(
                    snapshot.mostUsedPaymentMethod.amount / 1000
                  )}K, ${snapshot.mostUsedPaymentMethod.percentage}%)`
                : "-"}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Top Performing Gateway
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {snapshot.topPerformingGateway
                ? `${snapshot.topPerformingGateway.gateway} (${snapshot.topPerformingGateway.successRate}%)`
                : "-"}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Failed Transactions
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {snapshot.failedTransactions ?? "-"}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Retriable Failures
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {snapshot.retriableFailures ?? "-"}
            </div>
          </div>
        </div>
      </div>

      <div className="self-stretch p-5 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] inline-flex flex-col justify-start items-start gap-2.5">
        <div className="py-[5px] inline-flex justify-center items-center gap-2.5">
          <div className="text-black text-sm font-semibold leading-[18.2px]">
            Gateway Health
          </div>
        </div>
        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.5px] outline-[#E6E6E6]" />
        <div className="self-stretch flex justify-start items-start gap-10 pt-1">
          <div className="flex-1 flex flex-col gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Gateway</div>
            {(snapshot.gatewayHealth || []).map((g) => (
              <div key={g.gateway} className="text-[#232323] text-xs font-normal leading-[15.6px]">
                {g.gateway}
              </div>
            ))}
          </div>
          <div className="flex-1 flex flex-col gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Status</div>
            {(snapshot.gatewayHealth || []).map((g) => {
              const rate = Number(g.successRate || 0);
              let label = "Down";
              let dot = "bg-red-500";
              if (rate >= 70) {
                label = "Live";
                dot = "bg-green-500";
              } else if (rate >= 30) {
                label = "Slow";
                dot = "bg-yellow-400";
              }
              return (
                <div
                  key={g.gateway}
                  className="flex items-center gap-2 text-[#232323] text-xs font-normal leading-[15.6px]"
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
                  {label}
                </div>
              );
            })}
          </div>
          <div className="flex-1 flex flex-col items-end gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Success %</div>
            {(snapshot.gatewayHealth || []).map((g) => (
              <div key={g.gateway} className="text-[#232323] text-xs font-normal leading-[15.6px]">
                {Number(g.successRate).toFixed(1)}%
              </div>
            ))}
          </div>
          <div className="flex-1 flex flex-col items-end gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Business</div>
            {(snapshot.gatewayHealth || []).map((g) => (
              <div key={g.gateway} className="text-[#232323] text-xs font-normal leading-[15.6px]">
                {g.projectsInUse}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="self-stretch p-5 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] inline-flex flex-col justify-start items-start gap-2.5">
        <div className="py-[5px] inline-flex justify-center items-center gap-2.5">
          <div className="text-black text-sm font-semibold leading-[18.2px]">
            Active Businesses
          </div>
        </div>
        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.5px] outline-[#E6E6E6]" />
        <div className="self-stretch flex justify-start items-start gap-10 pt-1">
          <div className="flex-1 flex flex-col gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Business</div>
            {snapshot.activeProjects?.length ? (
              snapshot.activeProjects.map((p) => (
                <div
                  key={p._id}
                  className="text-[#232323] text-xs font-normal leading-[15.6px] truncate"
                >
                  {p.name}
                </div>
              ))
            ) : (
              <div className="text-[#232323] text-xs font-normal leading-[15.6px]">-</div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-end gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Volume</div>
            {snapshot.activeProjects?.length ? (
              snapshot.activeProjects.map((p) => (
                <div
                  key={p._id}
                  className="text-[#232323] text-xs font-normal leading-[15.6px]"
                >
                  {formatINRCompact(p.totalAmount)} / {p.transactionCount} txns
                </div>
              ))
            ) : (
              <div className="text-[#232323] text-xs font-normal leading-[15.6px]">-</div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-end gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Last Activity</div>
            {snapshot.activeProjects?.length ? (
              snapshot.activeProjects.map((p) => (
                <div
                  key={p._id}
                  className="text-[#757575] text-xs font-normal leading-[15.6px]"
                >
                  {formatLastActivity(p.lastActivity)}
                </div>
              ))
            ) : (
              <div className="text-[#757575] text-xs font-normal leading-[15.6px]">-</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const TransactionVolumeChart = React.memo(({ data, fromDate, toDate }) => {
  const [processedData, setProcessedData] = useState([]);
  const [viewMode, setViewMode] = useState('daily');

  useEffect(() => {
    if (!data || data.length === 0 || !fromDate || !toDate) {
      setProcessedData([]);
      return;
    }

    const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
    const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
    
    const start = new Date(fromYear, fromMonth - 1, fromDay);
    const end = new Date(toYear, toMonth - 1, toDay);
    
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    
    if (daysDiff <= 31) {
      setViewMode('daily');
      setProcessedData(data);
    } else if (daysDiff < 122) {
      setViewMode('weekly');
      setProcessedData(aggregateByWeek(data, fromDate));
    } else {
      setViewMode('monthly');
      setProcessedData(aggregateByMonth(data));
    }
  }, [data, fromDate, toDate]);

  const chartConfig = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return { barSize: 40, interval: 0, width: '100%' };
    }

    const len = processedData.length;
    let barSize = 40;
    let interval = 0;
    let width = '100%';

    if (len <= 5) barSize = 60;
    else if (len <= 7) barSize = 50;
    else if (len <= 10) barSize = 40;
    else if (len <= 15) barSize = 30;
    else if (len <= 31) barSize = 18;
    else barSize = 12;

    


    return { barSize, interval, width };
  }, [processedData, viewMode]);

  const yAxisDomains = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return { left: [0, 100], right: [0, 10] };
    }

    const maxAmount = Math.max(...processedData.map(d => d.totalAmount || 0));
    const maxCount = Math.max(...processedData.map(d => d.transactionCount || 0));
    
    return {
      left: [0, Math.ceil(maxAmount * 1.2) || 100],
      right: [0, Math.ceil(maxCount * 1.2) || 10]
    };
  }, [processedData]);

  const formatXAxisLabel = useCallback((dateStr) => {
    if (viewMode === 'monthly') {
      return formatDateLabel(dateStr, true);
    } else if (viewMode === 'weekly') {
      const date = new Date(dateStr + 'T00:00:00');
      const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
      const day = String(date.getDate()).padStart(2, '0');
      return `${month} ${day}`;
    } else {
      return formatDateLabel(dateStr, false);
    }
  }, [viewMode]);

  const chartTitle = useMemo(() => {
    if (viewMode === 'monthly') return 'Transaction Volume (Monthly)';
    if (viewMode === 'weekly') return 'Transaction Volume (Weekly)';
    return 'Transaction Volume (Daily)';
  }, [viewMode]);

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <div className="rounded-[10px] mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[15px] font-semibold text-black leading-[18.2px]">
          {chartTitle}
        </h2>
      </div>

      <div className="w-full overflow-x-auto">
        <div className="flex justify-start" style={{ width: typeof chartConfig.width === 'number' ? Math.min(chartConfig.width, 1400) : chartConfig.width, maxWidth: '1400px', height: '320px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={processedData} margin={{ top: 30, right: 60, left: 60, bottom: 20 }}>
              <defs>
                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E6E6E6" />

              <XAxis
                dataKey="date"
                tickFormatter={formatXAxisLabel}
                tick={{ fontSize: 11, fill: '#757575' }}
                axisLine={{ stroke: '#E6E6E6' }}
                tickLine={false}
                interval={chartConfig.interval}
              />

              <YAxis
                yAxisId="left"
                orientation="left"
                domain={yAxisDomains.left}
                tickFormatter={(v) => {
                  if (v >= 1000000) return `₹${(v / 1000000).toFixed(1)}M`;
                  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
                  return `₹${v}`;
                }}
                tick={{ fontSize: 11, fill: "#757575" }}
                axisLine={{ stroke: "#E6E6E6" }}
                tickLine={false}
                label={{ 
                  value: 'Amount Collected', 
                  angle: -90, 
                  position: 'insideLeft', 
                  style: { fontSize: 11, fill: '#232323', fontWeight: '500' } 
                }}
              />

              <YAxis
                yAxisId="right"
                orientation="right"
                domain={yAxisDomains.right}
                tick={{ fontSize: 11, fill: "#757575" }}
                axisLine={{ stroke: "#E6E6E6" }}
                tickLine={false}
                label={{ 
                  value: 'Transaction Count', 
                  angle: 90, 
                  position: 'insideRight', 
                  style: { fontSize: 11, fill: '#232323', fontWeight: '500' } 
                }}
              />

              <Tooltip 
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E6E6E6',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
                formatter={(value, name) => {
                  if (name === 'Amount Collected') {
                    return [`₹${value.toLocaleString()}`, name];
                  }
                  if (name === 'Transactions') {
                    return [value, name];
                  }
                  return null;
                }}
                labelFormatter={(label) => {
                  if (viewMode === 'weekly') {
                    return `Week of ${formatXAxisLabel(label)}`;
                  }
                  if (viewMode === 'monthly') {
                    return formatXAxisLabel(label);
                  }
                  return formatXAxisLabel(label);
                }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                height={36}
                iconType="rect"
                wrapperStyle={{ paddingBottom: '10px' }}
              />

              <Bar
                yAxisId="right"
                dataKey="transactionCount"
                name="Transactions"
                fill="#16a34a"
                radius={[6, 6, 0, 0]}
                barSize={chartConfig.barSize}
              >
                {processedData.length <= 31 && <LabelList content={CustomBarLabel} />}
              </Bar>

              <Area
                yAxisId="left"
                type="monotone"
                dataKey="totalAmount"
                fill="url(#colorAmount)"
                stroke="none"
                legendType="none"
              />

              <Line
                yAxisId="left"
                type="monotone"
                dataKey="totalAmount"
                name="Amount Collected"
                stroke="#2563eb"
                strokeWidth={2}
                dot={processedData.length <= 31 ? { r: 4, fill: "#ffffff", stroke: "#2563eb", strokeWidth: 2 } : false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
});

function TransactionTable({ data, page, totalPages, setPage, loading }) {
  const [copied, setCopied] = useState({ value: null, type: null });
  const [hoveredCell, setHoveredCell] = useState(null);
  const [selectedTxn, setSelectedTxn] = useState(null);


  const copyToClipboard = useCallback((value, type) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied({ value, type });
    setTimeout(() => setCopied({ value: null, type: null }), 2000);
  }, []);

  const handleInvoiceDownload = useCallback(async (txnId) => {
    try {
      const res = await api.get(
        `/api/payments/transaction/${txnId}?format=pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${txnId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Invoice download failed:", err);
    }
  }, []);
const getStatusStyles = useCallback((status) => {
  switch (status) {
    case "captured":
      return "text-green-700 bg-green-50 border border-green-600";
    case "authorized":
      return "text-yellow-700 bg-yellow-50 border border-yellow-500";
    case "created":
      return "text-orange-700 bg-orange-50 border border-orange-500";
    case "failed":
      return "text-red-600 bg-red-50 border border-red-500";
    default:
      return "text-gray-600 bg-gray-50 border border-gray-400";
  }
}, []);



  const visiblePages = useMemo(() => {
    const pages = [];
    const total = Math.max(1, totalPages || 1);

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      const start = Math.max(2, page - 1);
      const end = Math.min(total - 1, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < total - 2) pages.push("...");
      pages.push(total);
    }
    return pages;
  }, [page, totalPages]);

  const handleModalClose = useCallback(() => {
    setSelectedTxn(null);
  }, []);

  return (
    <div className="mt-6">
      <h2 className="text-[15px] font-semibold text-black mb-3 pb-6">
        Recent Transactions
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-[#EDEDED]">
              <th className="px-3 py-2 text-xs font-semibold">Transaction ID</th>
              <th className="px-3 py-2 text-xs font-semibold">Gateway ID</th>
              <th className="px-3 py-2 text-xs font-semibold">Business Name</th>
              <th className="px-3 py-2 text-xs font-semibold">Payment Method</th>
              <th className="px-3 py-2 text-xs font-semibold">Gateway Used</th>
              <th className="px-3 py-2 text-xs font-semibold">Status</th>
              <th className="px-3 py-2 text-xs font-semibold">Date</th>
              <th className="px-3 py-2 text-xs font-semibold">Amount</th>
              <th className="px-3 py-2 text-xs font-semibold text-right">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
  {/* Skeleton while loading */}
  {loading && (
    [...Array(6)].map((_, i) => (
      <tr key={`skeleton-${i}`} className="animate-pulse">
        {Array.from({ length: 9 }).map((_, j) => (
          <td key={j} className="px-3 py-3">
            <div className="h-3 bg-gray-200 rounded w-full" />
          </td>
        ))}
      </tr>
    ))
  )}

  {/* Empty state AFTER loading */}
  {/* Actual data */}
{!loading && data.length > 0 && data.map((tx) => (
  <tr
    key={tx._id}
    className="border-b border-[#F0F0F0] hover:bg-[#E8E8E8] cursor-pointer transition-colors duration-150"
    onClick={(e) => {
      if (
        !e.target.closest(".action-button") &&
        !e.target.closest(".copy-cell")
      ) {
        setSelectedTxn(tx);
      }
    }}
  >
    <td
      className="px-3 py-2 text-xs cursor-pointer relative copy-cell"
      onMouseEnter={() => setHoveredCell(`txn-${tx._id}`)}
      onMouseLeave={() => setHoveredCell(null)}
      onClick={(e) => {
        e.stopPropagation();
        copyToClipboard(tx.transactionId, "txn");
      }}
    >
      {hoveredCell === `txn-${tx._id}` ? "Copy" : maskId(tx.transactionId)}
    </td>

    <td
      className="px-3 py-2 text-xs cursor-pointer relative copy-cell"
      onMouseEnter={() => setHoveredCell(`gateway-${tx._id}`)}
      onMouseLeave={() => setHoveredCell(null)}
      onClick={(e) => {
        e.stopPropagation();
        copyToClipboard(tx.gatewayPaymentId, "gateway");
      }}
    >
      {hoveredCell === `gateway-${tx._id}` ? "Copy" : maskId(tx.gatewayPaymentId)}
    </td>

    <td className="px-3 py-2 text-xs">{tx.projectName}</td>
    <td className="px-3 py-2 text-xs capitalize">{tx.paymentMethod || "-"}</td>
    <td className="px-3 py-2 text-xs capitalize">{tx.gateway}</td>

    <td className="px-3 py-2">
      <span
        className={`px-2 py-0.5 rounded text-[11px] font-medium ${getStatusStyles(tx.status)}`}
      >
        {tx.status}
      </span>
    </td>

    <td className="px-3 py-2 text-xs text-gray-500">
      {new Date(tx.createdAt).toLocaleString("en-IN")}
    </td>

    <td className="px-3 py-2 text-xs font-semibold">
      ₹{tx.amount?.toLocaleString()}
    </td>

    <td className="px-3 py-2 text-right">
      <div className="flex justify-end gap-4">
        <img
          src={downloadIcon}
          className="w-4 cursor-pointer action-button"
          onClick={(e) => {
            e.stopPropagation();
            handleInvoiceDownload(tx._id);
          }}
        />
        <img
            src={archiveIcon}
            className="w-4 cursor-pointer hover:opacity-80 transition-opacity action-button"
            title="Archive"
            onClick={(e) => { e.stopPropagation(); }}
            alt="archive"            
            />
        <img
          src={arrowRightIcon}
          className="w-auto cursor-pointer hover:opacity-80 transition-opacity hover:scale-105 transition-transform action-button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTxn(tx);
          }}
        />
      </div>
    </td>
  </tr>
))}

</tbody>


        </table>
      </div>

      <div className="flex justify-center items-center gap-2 mt-8">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {visiblePages.map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className="w-10 h-10 flex items-center justify-center text-gray-400 text-sm">
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-all ${
                page === p
                  ? "bg-purple-600 text-white shadow-md"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {copied.value && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-up">
          <div className="bg-[#232323] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium">
              {copied.type === 'txn' ? 'Transaction ID' : 'Gateway ID'} copied to clipboard
            </span>
          </div>
        </div>
      )}

      {selectedTxn && (
        <TransactionViewModal txn={selectedTxn} onClose={handleModalClose} />
      )}
    </div>
  );
}

function RevenueTrendChart({ data, error }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border">
      <h2 className="text-lg font-bold mb-3">Revenue Trend (Last 30 Days)</h2>

      {error && <p className="text-red-500">{error}</p>}

      {data.length === 0 ? (
        <p className="text-gray-500">No revenue recorded.</p>
      ) : (
        <div className="w-full h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis dataKey="_id" />
              <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} />
              <Line
                type="monotone"
                dataKey="totalAmount"
                stroke="#3F00FF"
                strokeWidth={3}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

const formatDateLabel = (dateStr, isMonthView = false) => {
  if (isMonthView) {
    const [year, month] = dateStr.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  }
  
  const date = new Date(dateStr + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  return `${month} ${day}`;
};

const aggregateByWeek = (data, fromDate) => {
  if (!data || !data.length || !fromDate) return [];
  
  const startDate = new Date(fromDate);
  startDate.setHours(0, 0, 0, 0);
  
  const weekMap = new Map();
  
  data.forEach(item => {
    const itemDate = new Date(item.date + 'T00:00:00');
    itemDate.setHours(0, 0, 0, 0);
    
    const daysSinceStart = Math.floor((itemDate - startDate) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(daysSinceStart / 7);
    
    const weekStartDate = new Date(startDate);
    weekStartDate.setDate(startDate.getDate() + (weekNumber * 7));
    
    const year = weekStartDate.getFullYear();
    const month = String(weekStartDate.getMonth() + 1).padStart(2, '0');
    const day = String(weekStartDate.getDate()).padStart(2, '0');
    const weekKey = `${year}-${month}-${day}`;
    
    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, { 
        date: weekKey, 
        transactionCount: 0, 
        totalAmount: 0 
      });
    }
    
    const existing = weekMap.get(weekKey);
    existing.transactionCount += item.transactionCount || 0;
    existing.totalAmount += item.totalAmount || 0;
  });
  
  return Array.from(weekMap.values()).sort((a, b) => 
    a.date.localeCompare(b.date)
  );
};

const aggregateByMonth = (data) => {
  if (!data || !data.length) return [];
  
  const monthMap = new Map();
  
  data.forEach(item => {
    const date = new Date(item.date + 'T00:00:00');
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, { date: monthKey, transactionCount: 0, totalAmount: 0 });
    }
    
    const existing = monthMap.get(monthKey);
    existing.transactionCount += item.transactionCount || 0;
    existing.totalAmount += item.totalAmount || 0;
  });
  
  return Array.from(monthMap.values()).sort((a, b) => a.date.localeCompare(b.date));
};

const CustomBarLabel = (props) => {
  const { x, y, width, value } = props;
  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#232323"
      textAnchor="middle"
      fontSize={11}
      fontWeight="500"
    >
      {value}
    </text>
  );
};